import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

export interface Theme {
  cardId: string;
  primary_color_1?: string;
  primary_color_2?: string;
  coverImageUrl?: string;
}

export interface UpdateThemeResponse {
  success: boolean;
  message: string;
  data?: Theme;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly baseUrl = environment.apiUrl;

  // Reactive theme state (can be used across components)
  private themeSubject = new BehaviorSubject<Theme | null>(null);
  theme$ = this.themeSubject.asObservable();

  constructor(private http: HttpClient, private authService: AuthService) {}

  /** Generate HTTP headers with Bearer token */
  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.idToken;
    if (!token) throw new Error('User not authenticated');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  /**
   * Update theme colors and optional cover image for a card
   * PUT /api/cards/:cardId/theme
   */
  updateTheme(cardId: string, theme: Partial<Theme>): Observable<UpdateThemeResponse> {
    return this.http.put<UpdateThemeResponse>(
      `${this.baseUrl}/cards/${cardId}/theme`,
      theme,
      { headers: this.getAuthHeaders() }
    ).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.themeSubject.next(response.data);
        }
      })
    );
  }

  /** Clear cached theme state */
  clearCache(): void {
    this.themeSubject.next(null);
  }
}
